/**
 * React Query hooks for INKAR API
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import * as inkarApi from '../services/inkarApi'
import type {
  InkarFileInfo,
  InkarAnalyzeResponse,
  InkarIndicatorDbItem,
  InkarDataSourceInfo,
  InkarImportPreviewResponse,
  InkarImportExecuteRequest,
  InkarImportResponse,
  InkarImportStatusResponse,
} from '../services/inkarApi'

// ============================================================================
// Source Files
// ============================================================================

export function useInkarSources() {
  return useQuery<InkarFileInfo[]>({
    queryKey: ['inkar', 'sources'],
    queryFn: inkarApi.listSources,
    staleTime: 60 * 1000, // 1 minute
  })
}

// ============================================================================
// CSV Analysis
// ============================================================================

export function useInkarAnalyze(filePath: string | null) {
  return useQuery<InkarAnalyzeResponse>({
    queryKey: ['inkar', 'analyze', filePath],
    queryFn: () => inkarApi.analyzeCSV(filePath!),
    enabled: filePath !== null,
    staleTime: 5 * 60 * 1000, // 5 minutes (analysis is cached on backend too)
  })
}

// ============================================================================
// Database Indicators
// ============================================================================

export function useInkarIndicators(params?: {
  search?: string
  bereich?: string
  hide_rs_duplicates?: boolean
}) {
  return useQuery<InkarIndicatorDbItem[]>({
    queryKey: ['inkar', 'indicators', params],
    queryFn: () => inkarApi.listIndicators(params),
    staleTime: 60 * 1000,
  })
}

// ============================================================================
// Data Sources
// ============================================================================

export function useInkarDataSources() {
  return useQuery<InkarDataSourceInfo[]>({
    queryKey: ['inkar', 'data-sources'],
    queryFn: inkarApi.listDataSources,
    staleTime: 60 * 1000,
  })
}

// ============================================================================
// GV100 Editions
// ============================================================================

export function useGV100Editions() {
  return useQuery<inkarApi.GV100EditionInfo[]>({
    queryKey: ['inkar', 'editions'],
    queryFn: inkarApi.listGV100Editions,
    staleTime: 5 * 60 * 1000, // 5 minutes - editions don't change often
  })
}

// ============================================================================
// Import Preview
// ============================================================================

export function useInkarImportPreview(
  filePath: string | null,
  indicators: Record<string, number[]> | null
) {
  return useQuery<InkarImportPreviewResponse>({
    queryKey: ['inkar', 'preview', filePath, indicators],
    queryFn: () => inkarApi.previewImport(filePath!, indicators!),
    enabled: filePath !== null && indicators !== null && Object.keys(indicators).length > 0,
    staleTime: 30 * 1000,
  })
}

// ============================================================================
// Import Execution
// ============================================================================

export function useInkarExecuteImport() {
  const queryClient = useQueryClient()

  return useMutation<InkarImportResponse, Error, InkarImportExecuteRequest>({
    mutationFn: inkarApi.executeImport,
    onSuccess: () => {
      // Invalidate related caches
      queryClient.invalidateQueries({ queryKey: ['inkar', 'data-sources'] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'stats'] })
    },
  })
}

export function useInkarImportStatus(
  jobId: string | null,
  options?: { refetchInterval?: number | false }
) {
  return useQuery<InkarImportStatusResponse>({
    queryKey: ['inkar', 'import-status', jobId],
    queryFn: () => inkarApi.getImportStatus(jobId!),
    enabled: jobId !== null,
    refetchInterval: options?.refetchInterval ?? false,
  })
}

// ============================================================================
// Download
// ============================================================================

export function useInkarStartDownload() {
  return useMutation<
    inkarApi.InkarDownloadResponse,
    Error,
    inkarApi.InkarDownloadRequest
  >({
    mutationFn: inkarApi.startDownload,
  })
}

export function useInkarDownloadStatus(
  jobId: string | null,
  options?: { refetchInterval?: number | false }
) {
  return useQuery<inkarApi.InkarDownloadStatusResponse>({
    queryKey: ['inkar', 'download-status', jobId],
    queryFn: () => inkarApi.getDownloadStatus(jobId!),
    enabled: jobId !== null,
    refetchInterval: options?.refetchInterval ?? false,
  })
}

// ============================================================================
// Cache Management
// ============================================================================

export function useInkarCacheStats() {
  return useQuery<inkarApi.CacheStatsResponse>({
    queryKey: ['inkar', 'cache-stats'],
    queryFn: inkarApi.getCacheStats,
    staleTime: 30 * 1000, // 30 seconds
  })
}

export function useInkarDeleteCacheEntry() {
  const queryClient = useQueryClient()

  return useMutation<{ success: boolean; deleted: string }, Error, string>({
    mutationFn: inkarApi.deleteCacheEntry,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inkar', 'cache-stats'] })
    },
  })
}

export function useInkarClearAllCache() {
  const queryClient = useQueryClient()

  return useMutation<{ success: boolean; deleted_count: number }, Error, void>({
    mutationFn: inkarApi.clearAllCache,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inkar', 'cache-stats'] })
      // Also invalidate analysis queries since cache was cleared
      queryClient.invalidateQueries({ queryKey: ['inkar', 'analyze'] })
    },
  })
}

export function useInkarRefreshCacheEntry() {
  const queryClient = useQueryClient()

  return useMutation<
    { success: boolean; cache_key: string; indicator_count: number; total_rows: number },
    Error,
    string
  >({
    mutationFn: inkarApi.refreshCacheEntry,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inkar', 'cache-stats'] })
      queryClient.invalidateQueries({ queryKey: ['inkar', 'analyze'] })
    },
  })
}

// ============================================================================
// Raumgliederungen
// ============================================================================

export function useRaumgliederungenStatus() {
  return useQuery<inkarApi.RaumgliederungenStatus[]>({
    queryKey: ['inkar', 'raumgliederungen-status'],
    queryFn: inkarApi.getRaumgliederungenStatus,
    staleTime: 60 * 1000, // 1 minute
  })
}

export function useRaumgliederungenImport() {
  const queryClient = useQueryClient()

  return useMutation<
    inkarApi.RaumgliederungenImportResponse,
    Error,
    inkarApi.RaumgliederungenImportRequest
  >({
    mutationFn: inkarApi.importRaumgliederungen,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inkar', 'raumgliederungen-status'] })
    },
  })
}
